import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Timer, Loader2, ArrowRight, Ban, MapPin } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { clearHoldTimer } from "@/components/noxn/AssessmentForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentReturn({ variant = "success" }) {
    usePageMeta({
        title: variant === "cancel" ? "Payment cancelled · NOXN" : "Payment received · NOXN",
        description: variant === "cancel" ? "Your NOXN checkout was cancelled." : "Your NOXN screen holds are being confirmed.",
    });
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
        if (!sessionId) {
            setState({ loading: false, data: null, error: "Missing session_id in URL." });
            return;
        }
        let cancelled = false;
        let stopped = false;
        const fetchOnce = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (cancelled) return;
                setState({ loading: false, data, error: null });
                // Stop polling once we're paid — bookings are then confirmed.
                if (data?.payment_status === "paid") stopped = true;
            } catch (e) {
                if (cancelled) return;
                setState({ loading: false, data: null, error: e?.response?.data?.detail || "Could not load payment status." });
            }
        };
        fetchOnce();
        const iv = setInterval(() => { if (!stopped) fetchOnce(); }, 4000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [sessionId]);

    const bookings = state.data?.bookings || [];
    const holds = useMemo(() => bookings.filter((b) => b.status === "hold"), [bookings]);
    const confirmed = useMemo(() => bookings.filter((b) => b.status === "confirmed"), [bookings]);
    const waitlisted = useMemo(() => bookings.filter((b) => b.status === "waitlist"), [bookings]);
    const paid = state.data?.payment_status === "paid";
    const isCancel = variant === "cancel";
    const isPlays = state.data?.kind === "plays" || params.get("kind") === "plays";

    // Once payment is confirmed the hold timer is obsolete — clear it so the
    // wizard doesn't show a stale hold banner.
    useEffect(() => {
        if (paid) clearHoldTimer();
    }, [paid]);

    // Plays top-up → auto-return to the campaign wizard so a pending
    // booking can finalise against the new balance (Wave 3 wiring).
    useEffect(() => {
        if (!paid || !isPlays) return;
        // Only auto-return when the user was in a wizard session (has a saved draft)
        let hasWizard = false;
        try { hasWizard = !!sessionStorage.getItem("noxn.wizard.v2"); } catch (e) { /* ignore */ }
        if (!hasWizard) return;
        const t = setTimeout(() => {
            navigate("/account/book?resumed_from_topup=1", { replace: true });
        }, 1500);
        return () => clearTimeout(t);
    }, [paid, isPlays, navigate]);

    return (
        <div className="min-h-screen bg-[#0F1E3B] text-white relative">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />
            <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16">
                <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: [0.16,1,0.3,1] }}>
                    <div className={`font-mono-tech text-[11px] uppercase tracking-[0.35em] mb-4 flex items-center gap-3 ${isCancel ? "text-slate-400" : "text-[#FF5A00]"}`}>
                        <span className={`inline-block w-6 h-px ${isCancel ? "bg-slate-400" : "bg-[#FF5A00]"}`} />
                        {isCancel ? "Payment cancelled" : (paid ? "Payment received" : "Processing payment")}
                    </div>
                    <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                        {isCancel ? "No worries." : (paid ? "You're locked in." : "Just a moment.")}
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm max-w-xl" data-testid="payment-return-lede">
                        {isCancel
                            ? (holds.length > 0
                                ? "Your card was not charged. Your screens stay reserved for the time shown below — restart the request to complete payment before the hold ends."
                                : "Your screen holds have been released. Your card was not charged. You can restart the request whenever you're ready.")
                            : paid
                                ? "We've confirmed your screen reservations. A NOXN campaign director will reach out shortly."
                                : "We're finalising your booking with Stripe. Your screens are on a 20-minute hold — if payment doesn't complete in time, they'll release automatically."
                        }
                    </p>
                </motion.div>

                {state.loading && (
                    <div className="mt-10 flex items-center gap-3 text-slate-400 text-sm" data-testid="payment-return-loading">
                        <Loader2 className="animate-spin" size={16} /> Checking status…
                    </div>
                )}
                {state.error && (
                    <div className="mt-10 border border-[#FF5A00]/40 bg-[#FF5A00]/10 p-4 text-sm text-[#FF5A00]" data-testid="payment-return-error">
                        {state.error}
                    </div>
                )}

                {state.data && (
                    <div className="mt-10 space-y-6" data-testid="payment-return-body">
                        {holds.length > 0 && !paid && (
                            <Group
                                title="Held for you"
                                tone="amber"
                                icon={<Timer size={14} />}
                                items={holds}
                                showCountdown
                                testid="payment-return-holds"
                            />
                        )}
                        {confirmed.length > 0 && (
                            <Group
                                title="Confirmed"
                                tone="emerald"
                                icon={<Check size={14} />}
                                items={confirmed}
                                testid="payment-return-confirmed"
                            />
                        )}
                        {waitlisted.length > 0 && (
                            <Group
                                title="On the waitlist"
                                tone="orange"
                                icon={<Ban size={14} />}
                                items={waitlisted}
                                testid="payment-return-waitlisted"
                            />
                        )}
                        {bookings.length === 0 && (
                            <p className="text-sm text-slate-400" data-testid="payment-return-no-bookings">
                                No screens were reserved for this request.
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-12 flex flex-wrap items-center gap-3">
                    <Link to="/" data-testid="payment-return-home"
                        className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white transition">
                        Back to noxn.com.au <ArrowRight size={12} />
                    </Link>
                    {isCancel && (
                        <button onClick={() => navigate("/#request", { replace: true })}
                            data-testid="payment-return-retry"
                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                            Restart request <ArrowRight size={12} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const Group = ({ title, icon, tone, items, showCountdown, testid }) => {
    const toneMap = {
        amber: "border-amber-400/40 text-amber-300",
        emerald: "border-emerald-400/50 text-emerald-300",
        orange: "border-[#FF5A00]/50 text-[#FF5A00]",
    };
    return (
        <div className={`border ${toneMap[tone]} bg-[#1B2E55]/60 p-5`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-4">
                <span className={toneMap[tone].split(" ").pop()}>{icon}</span>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em]">/ {title}</div>
                <div className="ml-auto text-xs font-mono-tech text-slate-500">{items.length}</div>
            </div>
            <ul className="space-y-2">
                {items.map((b) => (
                    <li key={b.id} className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2">
                        <div className="min-w-0">
                            <div className="text-sm text-white truncate">{b.screen_name}</div>
                            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 flex items-center gap-1">
                                <MapPin size={10} /> {b.screen_city} · {b.start_date} → {b.end_date}
                            </div>
                        </div>
                        {showCountdown && b.hold_expires_at ? (
                            <Countdown iso={b.hold_expires_at} />
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const Countdown = ({ iso }) => {
    const [tick, setTick] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const ms = new Date(iso).getTime() - tick;
    if (ms <= 0) {
        return <span className="text-slate-500 text-[10px] font-mono-tech uppercase tracking-widest">Expired</span>;
    }
    const s = Math.floor(ms / 1000);
    const mm = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return (
        <span
            data-testid="payment-return-countdown"
            className="text-amber-300 font-mono-tech text-sm tabular-nums inline-flex items-center gap-1"
        >
            <Timer size={12} /> {mm}:{ss}
        </span>
    );
};
